import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Monitor, ShieldOff, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/**
 * Admin-side device history modal. Shows exactly how a reader got warned or
 * banned - every browser/OS row, the two warning stamps, and the ban stamp
 * with device count at each strike. Read-only; no revoke controls from the
 * admin side yet (would just get re-added on the reader's next hit).
 */
const UserDevicesModal = ({ token, user, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${API}/api/admin/users/${user.id}/devices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Could not load device history.');
      setData(await r.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { load(); }, [load]);

  if (!user) return null;

  const w = data?.warnings || {};
  const strike =
    w.warning_2_at ? 2 :
    w.warning_1_at ? 1 :
    0;
  const banned = user?.plan_status === 'banned' || !!data?.user?.banned_at;
  const active = data?.active_in_window ?? 0;
  const allowed = data?.policy?.allowed_devices ?? 3;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="admin-user-devices-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl my-8 bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-zinc-800">
          <div>
            <p className="text-red-500 text-[10px] font-black uppercase tracking-[0.3em] mb-1">Device history</p>
            <h2 className="text-white text-xl font-black flex items-center gap-2">
              <Monitor className="h-5 w-5" /> {user.email}
            </h2>
            <p className="text-zinc-500 text-xs mt-1">
              Policy: {allowed} devices allowed in any {data?.policy?.window_days ?? 14}-day window.
              Currently active: <span className={active > allowed ? 'text-red-300 font-black' : 'text-white font-black'}>{active}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-user-devices-close"
            className="text-zinc-500 hover:text-white p-1"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {(strike > 0 || banned) && (
          <div className="px-5 pt-5">
            <div
              className={
                'rounded p-3 border ' +
                (banned
                  ? 'bg-red-950/60 border-red-500/60 text-red-100'
                  : strike === 2
                  ? 'bg-orange-950/60 border-orange-500/60 text-orange-100'
                  : 'bg-yellow-950/60 border-yellow-500/60 text-yellow-100')
              }
              data-testid="admin-user-devices-ladder"
            >
              <p className="text-[11px] font-black uppercase tracking-widest flex items-center gap-2 mb-2">
                {banned ? <ShieldOff className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                {banned ? 'BANNED — 3rd strike' : strike === 2 ? 'FINAL WARNING issued' : 'Warning 1 issued'}
              </p>
              <ul className="text-xs space-y-1 leading-relaxed">
                {w.warning_1_at && (
                  <li data-testid="admin-user-strike-1">
                    <strong>Strike 1</strong> — {fmt(w.warning_1_at)}
                    {w.devices_at_warning_1 != null && <> · devices seen: <strong>{w.devices_at_warning_1}</strong></>}
                  </li>
                )}
                {w.warning_2_at && (
                  <li data-testid="admin-user-strike-2">
                    <strong>Strike 2</strong> — {fmt(w.warning_2_at)}
                    {w.devices_at_warning_2 != null && <> · devices seen: <strong>{w.devices_at_warning_2}</strong></>}
                  </li>
                )}
                {banned && (
                  <li data-testid="admin-user-strike-3">
                    <strong>Strike 3 (Ban)</strong> — {fmt(data?.user?.banned_at)}
                    {w.devices_at_ban != null && <> · devices seen: <strong>{w.devices_at_ban}</strong></>}
                    {data?.user?.banned_reason && <> · reason: <code className="text-red-200">{data.user.banned_reason}</code></>}
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}

        <div className="p-5">
          {err && <p className="text-red-400 text-xs mb-3">{err}</p>}
          {loading && !data && (
            <p className="text-zinc-500 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading device history…</p>
          )}

          {data && data.devices.length === 0 && (
            <p className="text-zinc-500 text-sm">No devices recorded for this account yet.</p>
          )}

          {data && data.devices.length > 0 && (
            <div className="border border-zinc-900 rounded overflow-hidden" data-testid="admin-user-devices-list">
              <table className="w-full text-xs">
                <thead className="bg-zinc-900/60 text-zinc-500 text-[10px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-3 py-2">Device</th>
                    <th className="text-left px-3 py-2">First seen</th>
                    <th className="text-left px-3 py-2">Last active</th>
                    <th className="text-left px-3 py-2 hidden md:table-cell">IPs</th>
                  </tr>
                </thead>
                <tbody>
                  {data.devices.map((d) => (
                    <tr key={d.client_id} className="border-t border-zinc-900" data-testid={`admin-user-device-row-${d.client_id}`}>
                      <td className="px-3 py-2 text-white font-black align-top">
                        <div>{d.label}</div>
                        <div className="text-zinc-600 text-[10px] font-mono truncate max-w-[220px]" title={d.user_agent}>{d.user_agent}</div>
                        <div className="text-zinc-700 text-[10px] font-mono truncate max-w-[220px]" title={d.client_id}>cid: {d.client_id.slice(0, 12)}…</div>
                      </td>
                      <td className="px-3 py-2 text-zinc-400 align-top">{fmt(d.first_seen)}</td>
                      <td className="px-3 py-2 text-zinc-400 align-top">{fmt(d.last_seen)}</td>
                      <td className="px-3 py-2 text-zinc-500 font-mono hidden md:table-cell align-top">
                        {d.first_ip || '-'}
                        {d.last_ip && d.last_ip !== d.first_ip && <> → {d.last_ip}</>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-zinc-800 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-black uppercase tracking-widest bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white px-4 py-2 rounded"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDevicesModal;
